/**
 * `charts/scale.ts` — the axis arithmetic and the margins every chart shares (launch round,
 * 7 Oct 2026: "very clean and neatly aligned, labelled x and y axes").
 *
 * Two things live here and nothing else:
 *
 * * `niceTicks(max, steps)` — a value axis from 0 to a round maximum in 4 or 5 equal steps,
 *   so a tick row reads 0 · 250 · 500 · 750 · 1,000 and never 0 · 255 · 510.
 * * the margin constants. A chart's left label column, its right margin and the bands its
 *   axis titles sit in come from here, so two cards stacked on one page put their axes on
 *   the same vertical line.
 */

/** Font size of an axis title ("Share of households (%)"), in the chart's own units. */
export const AXIS_LABEL_SIZE = 11;
/** Width of the band the rotated y-axis title stands in, left of the label column. */
export const AXIS_Y_BAND = 16;
/** Height of the band the x-axis title sits in, under the tick row. */
export const AXIS_X_BAND = 16;
/** The left label column of a row chart (country, watershed, indicator names). */
export const CHART_LABEL_COL = 76;
/** The right margin past the plot, shared by the row and column charts. */
export const CHART_PAD_RIGHT = 10;
/** The axis-title ink, `--ink-2`. A literal as well as a class, for SVG exported as a file. */
export const AXIS_LABEL_INK = '#55614F';

/** Width one character of an axis title takes, as a share of its font size. */
export const AXIS_CHAR_RATIO = 0.55;

/** "quantity (unit)" — an axis title; the quantity alone when there is no unit to name. */
export function axisTitle(quantity: string, unit?: string | null): string {
  const u = unit?.trim();
  return u ? `${quantity} (${u})` : quantity;
}

/**
 * An axis title that fits `length` units, or the start of it plus an ellipsis. Measured
 * arithmetically (as `BarsH` measures its row labels) so it is right in jsdom and on the
 * first render; the chart puts the full title on a `<title>` beside it.
 */
export function fitAxisTitle(text: string, length: number): string {
  const max = Math.max(4, Math.floor(length / (AXIS_LABEL_SIZE * AXIS_CHAR_RATIO)));
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/** The bands a chart adds for its axis titles: nothing for a title it was not given. */
export function axisBands(xLabel?: string | null, yLabel?: string | null): { left: number; bottom: number } {
  return { left: yLabel ? AXIS_Y_BAND : 0, bottom: xLabel ? AXIS_X_BAND : 0 };
}

/** 1, 2, 2.5 and 5 times a power of ten: the steps a reader can add up in their head. */
const LADDER = [1, 2, 2.5, 5] as const;

/**
 * Tick values from 0 to the smallest round maximum at or above `max`, in `steps` or
 * `steps + 1` equal intervals — whichever reaches the lower top (the fewer steps on a tie).
 *
 * `niceTicks(100)` is 0/25/50/75/100, the percentage axis; `niceTicks(44858)` is
 * 0/10,000/…/50,000 rather than 0/20,000/…/80,000. A maximum that is zero,
 * negative or not a number gets the 0-1 axis, because a chart with no scale at all reads as
 * a broken one.
 */
export function niceTicks(max: number, steps = 4): number[] {
  const n = Math.max(1, Math.round(steps));
  const top = Number.isFinite(max) && max > 0 ? max : 1;
  let best: { step: number; count: number } | null = null;
  const start = Math.floor(Math.log10(top / (n + 1))) - 1;
  for (let exp = start; exp <= start + 3; exp += 1) {
    for (const factor of LADDER) {
      const step = factor * 10 ** exp;
      for (const count of [n, n + 1]) {
        if (step * count < top - 1e-9 * top) continue;
        const reach = step * count;
        const bestReach = best ? best.step * best.count : Infinity;
        // The lower top wins; on a tie, the fewer steps (100 is 0/25/…/100, not 0/20/…/100).
        const better =
          reach < bestReach * (1 - 1e-9) ||
          (Math.abs(reach - bestReach) <= bestReach * 1e-9 && best !== null && count < best.count);
        if (better) best = { step, count };
      }
    }
  }
  const { step, count } = best ?? { step: top / n, count: n };
  // Multiply rather than add: repeated addition of 0.1 drifts, and a tick printed as
  // 0.30000000000000004 is exactly the untidiness this helper exists to remove.
  return Array.from({ length: count + 1 }, (_, i) => Number((step * i).toPrecision(12)));
}
